import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Container, Row, Col } from "react-bootstrap";
import ModalForm from "../components/ModalForm";
import ClinicPatientForm from "../components/ClinicPatientForm";
import PatientHeader from "../components/PatientHeader";
import SubmitButton from "../components/SubmitButton";
import Button from "../components/Button";
import styles from "./ClinicPatient.module.scss";
import DashboardButtonsCard from "../components/DashboardButtonsCard";
import Loader from "../components/Loader";
import { alertModal, confirmModal } from "../utils/modalController";
import ErrorPage from "./ErrorPage";
import { fetchAuth } from "../utils/fetchAuth";

const apiUrl = process.env.REACT_APP_API_URL || '';

export default function ClinicPatient() {
    const [user, setUser] = useState<User | null>(null);
    const [age, setAge] = useState<number | null>(null);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [modalTitle, setModalTitle] = useState("Editar paciente");
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [errorCode, setErrorCode] = useState<number | null>(null);

    const { id } = useParams();

    const navigate = useNavigate();

    useEffect(() => {
        const fetchUser = async () => {
            try {
                const response = await fetchAuth(`${apiUrl}/consultorio/patients/${id}`, {
                    method: "GET",
                });

                if (!response.ok) {
                    const error_json = await response.json();
                    setErrorCode(response.status);
                    throw new Error(error_json.message || "Erro desconhecido");
                }

                const dataPatient = await response.json();

                if(!dataPatient.user){
                    throw new Error("Usuário não encontrado");
                }

                const userRes = await fetchAuth(`${apiUrl}/consultorio/users/${dataPatient.user.id}`);

                if (!userRes.ok) {
                    const error_json = await userRes.json();
                    setErrorCode(userRes.status);
                    throw new Error(error_json.message || "Erro desconhecido");
                }

                const userData = await userRes.json();
                setUser(
                    {
                        ...userData,
                        patient: dataPatient
                    }
                );
            } catch (error) {
                console.error("Erro:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchUser();
    }, [showModal, id]); // Adicione showModal como dependência para atualizar a lista de usuários quando o modal for fechado


    useEffect(() => {
        if (user && user.birthday) {
            const birthDate = new Date(user.birthday);
            const ageDifMs = Date.now() - birthDate.getTime();
            const ageDate = new Date(ageDifMs);
            setAge(Math.abs(ageDate.getUTCFullYear() - 1970));
        }
    }, [user]);

    if (errorCode) return <ErrorPage code={errorCode} />;

    return (
        <>
            <Container className={styles.container}>
                <h2 className={styles.title}>Consultório</h2>
                {loading ? (
                    <Loader />
                ) : (
                    <>
                        <Row className={styles["row-card"]}>
                            <Col>
                                <PatientHeader
                                    user={user!}
                                    age={age}
                                    breadcrumbItems={[
                                        { label: "Consultório", href: "/consultorio" },
                                        { label: "Pacientes", href: "/consultorio/pacientes" },
                                        { label: user?.name || "Paciente" },
                                    ]}
                                />
                            </Col>
                        </Row>
                    </>
                )}
            </Container>
            <DashboardButtonsCard
                buttons={[
                    { text: "Consultas", onClick: () => { navigate(`/consultorio/pacientes/${id}/consultas`) } },
                    { text: "Planos Alimentares", onClick: () => { navigate(`/consultorio/pacientes/${id}/planos-alimentares`) } },
                    { text: "Contratos", onClick: () => { navigate(`/consultorio/pacientes/${id}/contratos`) } },
                    { text: "Formulários", onClick: () => { navigate(`/consultorio/pacientes/${id}/formularios`) } },
                    {
                        text: "Dados do paciente", onClick: () => {
                            setModalTitle("Dados do paciente");
                            setShowModal(true);
                        }
                    },
                ]}
            />
            <ModalForm
                title={modalTitle}
                show={showModal}
                onHide={() => { setShowModal(false) }}
                onSubmit={(e) => {
                    e.preventDefault();
                    setSaving(true);
                    const formData = new FormData(e.currentTarget);
                    const formDataJson = Object.fromEntries(formData.entries());
                    fetchAuth(apiUrl + "/consultorio/patients/" + id, {
                        method: "PUT",
                        headers: {
                            "Content-Type": "application/json",
                        },
                        body: JSON.stringify(formDataJson),
                    })
                        .then(async (response) => {
                            if (!response.ok) {
                                const error_json = await response.json();
                                throw new Error(error_json.message || "Erro desconhecido");
                            }
                            return response.json();
                        })
                        .then((response_json) => {
                            setUser(response_json);
                            setShowModal(false);
                        })
                        .catch((error) => {
                            console.error("Erro ao atualizar paciente:", error);
                            alertModal("Erro ao atualizar paciente: " + error.message);
                        })
                        .finally(() => {
                            setSaving(false);
                        });
                }}
                footerButtons={[
                    <Button
                        label="Cancelar"
                        className={styles['buttons']}
                        onClick={() => setShowModal(false)}
                        variant="secondary"
                        disabled={saving}
                    />,
                    <Button
                        className={[styles['buttons'], styles['remove-button'], user ? '' : 'd-none'].join(' ')}
                        onClick={async () => {
                            if (!await confirmModal("Você tem certeza que deseja remover este paciente? Esta ação não pode ser desfeita.")) return;
                            if (user) {
                                setDeleting(true);
                                fetchAuth(apiUrl + "/consultorio/patients/" + user?.patient?.id, {
                                    method: "DELETE",
                                })
                                    .then(async (response) => {
                                        if (!response.ok) {
                                            const error_json = await response.json();
                                            throw new Error(error_json.message || "Erro desconhecido");
                                        }
                                        return response.json();
                                    })
                                    .then((response_json) => {
                                        setShowModal(false);
                                        navigate("/consultorio/pacientes");
                                    })
                                    .catch((error) => {
                                        console.error("Erro ao remover usuário:", error);
                                        alertModal("Erro ao remover usuário: " + error.message);
                                    })
                                    .finally(() => {
                                        setDeleting(false);
                                    });
                            }
                        }}
                        variant="secondary"
                        disabled={saving || !user || deleting}
                    >
                        {deleting ? "Removendo..." : "Remover"}
                    </Button>,
                    <SubmitButton
                        className={styles['buttons']}
                        disabled={saving || deleting}
                    >
                        {saving ? "Salvando..." : "Salvar"}
                    </SubmitButton>,
                ]}
            >
                <ClinicPatientForm patient={user?.patient?.id ? { id: user?.patient?.id } : undefined} />
            </ModalForm>
        </>
    );
}
